import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import ConstructionIcon from '@mui/icons-material/Construction';
import GavelIcon from '@mui/icons-material/Gavel';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    {
        id: 'introduction',
        label: 'はじめに：ルームシェア生活と生活音トラブル',
    },
    {
        id: 'types-of-noise',
        label: '1. 生活音の種類とトラブルの原因',
    },
    {
        id: 'goods',
        label: '2. 防音グッズ・音対策アイテムの活用法',
    },
    {
        id: 'communication',
        label: '3. コミュニケーションでトラブル回避',
    },
    {
        id: 'rules',
        label: '4. ルール作りの重要性と実践例',
    },
    {
        id: 'expert-tips',
        label: '5. 専門家アドバイス＆まとめ',
    },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 0.5, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.98rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに：ルームシェア生活と生活音トラブル
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 2 }}>
                    近年、都心部を中心に<strong>ルームシェア</strong>がますます人気を集めています。家賃の節約や、さまざまな人と出会える楽しさが魅力ですが、一方で<strong>生活音</strong>をめぐるトラブルは非常に多く、その対策はルームシェア生活の満足度を大きく左右します。<br />
                    本記事では、<Chip label="生活音の種類" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />
                    <Chip label="防音グッズ" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />
                    <Chip label="コミュニケーション" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />
                    <Chip label="ルール作り" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />
                    など、実際に役立つノウハウを専門的視点でわかりやすく解説します。
                </Typography>
                <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, mb: 2 }}>
                    <img
                        src="/images/blog/living-dining_roomshare-seikatsuon-trouble-countermeasures.jpg"
                        alt="明るい共有リビングでルームメイト同士が食事や会話を楽しむ様子。木目調のテーブルとカラフルな椅子が並び、穏やかな雰囲気が伝わる写真。和やかな空間で生活音が自然に混じり合う状況をイメージできる。"
                        style={{ width: '100%', objectFit: 'cover', maxHeight: 260 }}
                    />
                </Paper>
                <Typography variant="body2" color="textSecondary">
                    ルームシェアの楽しさは、音のストレスが少ない環境作りから始まります。
                </Typography>
            </Box>

            {/* 1. 生活音の種類とトラブルの原因 */}
            <Box id="types-of-noise" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    1. 生活音の種類とトラブルの原因
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            ルームシェアで頻繁に話題となる「生活音」。日常の動作や会話の音は避けて通れないものですが、これが意外なストレス源になることも。<br />
                            トラブルを防ぐためには、まず<strong>どんな音が問題になりやすいのか</strong>、しっかり把握しておきましょう。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon><VolumeUpIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>話し声・電話の音</b>}
                                    secondary="深夜や早朝の会話、長電話、笑い声などが響きやすい"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><SpeakerNotesIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>テレビ・音楽・ゲーム音</b>}
                                    secondary="壁越しに音が漏れやすく、趣味や生活リズムの違いが露呈しやすい"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ConstructionIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>掃除機・洗濯機・家電音</b>}
                                    secondary="早朝・夜間の使用や、振動音が床や壁を伝わることでトラブルに"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><EmojiPeopleIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>足音・ドアの開閉・物音</b>}
                                    secondary="無意識に大きくなりがちな生活動作音"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            生活リズムの違いが、生活音トラブルの大きな要因。<strong>早寝早起き型と夜型</strong>が混在する場合は特に注意が必要です。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                            
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 2. 防音グッズ・音対策アイテムの活用法 */}
            <Box id="goods" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    2. 防音グッズ・音対策アイテムの活用法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「気をつけていても生活音が漏れてしまう…」そんなときは<strong>防音グッズ</strong>やアイデアグッズの活用が効果的。実際にルームシェア経験者からも高評価のアイテムを紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ConstructionIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>防音マット・カーペット</Typography>
                                </Box>
                                <Typography variant="body2">
                                    足音や物を落としたときの衝撃音を吸収。特にフローリングの部屋におすすめ。洗濯機や冷蔵庫の下にも敷くと効果的。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <VolumeUpIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>遮音カーテン・ドアストッパー</Typography>
                                </Box>
                                <Typography variant="body2">
                                    窓やドアから漏れる音を軽減。隙間テープと併用するとさらに効果UP。部屋ごとに取り付けやすい手軽さも魅力。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxWidth: 400, mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SpeakerNotesIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>イヤホン・ヘッドホンの活用</Typography>
                                </Box>
                                <Typography variant="body2">
                                    テレビや音楽鑑賞はなるべくイヤホンで。夜間や早朝は特に有効。ワイヤレスなら移動もスムーズ。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ maxWidth: 400 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TipsAndUpdatesIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>ドアや壁の隙間対策アイテム</Typography>
                                </Box>
                                <Typography variant="body2">
                                    隙間テープやフォーム材を使って、音の通り道を遮断。施工不要のアイテムも増えており、賃貸でも手軽に導入可能。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, mb: 1 }}>
                    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                        
                    </Paper>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    防音グッズは「みんなで使うもの」と「個人で用意するもの」を分けて、導入時はルームメイトと共有・相談しておくと良いでしょう。
                </Typography>
            </Box>

            {/* 3. コミュニケーションでトラブル回避 */}
            <Box id="communication" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    3. コミュニケーションでトラブル回避
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    防音対策と同じくらい重要なのが<strong>コミュニケーション</strong>。音に関するストレスや不満は、我慢せずに早めに共有することが大切です。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, mb: 2 }}>
                        
                    </Paper>
                </Box>
                <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
                    生活音に関するコミュニケーションのポイント
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><EmojiPeopleIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「気になったらすぐ相談」を基本に</b>}
                            secondary="我慢せず、なるべく早く小さな悩みも伝え合う"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><SpeakerNotesIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>伝え方は「お願い」ベースで</b>}
                            secondary="「うるさい」と責めるのではなく、「もう少し静かにしてもらえると助かる」など柔らかく伝える"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                        <ListItemText
                            primary={<b>自分も気をつけている姿勢を共有</b>}
                            secondary="「私も気を付けてみるので一緒に対策しませんか？」など、協力スタンスを示す"
                        />
                    </ListItem>
                </List>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    言い出しにくい内容ほど、チャットやノートなど「文章」で伝えるのも有効です。定期的なミーティングもおすすめ。
                </Typography>
            </Box>

            {/* 4. ルール作りの重要性と実践例 */}
            <Box id="rules" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    4. ルール作りの重要性と実践例
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアを長く快適に続けるためには、<strong>生活音に関するルール</strong>を明確にしておくことが不可欠です。曖昧なままでは、トラブルのもとになってしまいます。
                </Typography>
                <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
                    よくある生活音ルール例
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><MeetingRoomIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>テレビや音楽のボリューム制限</b>}
                            secondary="夜22時以降はイヤホン利用／リビングは音量◯◯以下など"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>掃除・洗濯の時間帯指定</b>}
                            secondary="早朝7時前や夜22時以降の利用を控える"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ConstructionIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>ドア・家具の開閉音対策</b>}
                            secondary="ゆっくり静かに閉める／深夜早朝は極力控える"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><InfoIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>来客や友人招待の事前連絡</b>}
                            secondary="日中のみ許可／音が気になる場合は事前に相談"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 2 }}>
                    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                        <img
                            src="/images/blog/share-rules_roomshare-seikatsuon-trouble-countermeasures.jpg"
                            alt="ルームメイトたちが壁に貼った『生活音ルール表』を指差しながら話し合っているシーン。明るい室内で、ルールの可視化と合意形成の雰囲気をリアルに伝える写真。"
                            style={{ width: '100%', objectFit: 'cover', maxHeight: 180 }}
                        />
                    </Paper>
                </Box>
                <Typography variant="body2" color="textSecondary">
                    ルールは「守れない場合はどうするか」も含めて決めておくと、トラブル時の対処がスムーズになります。
                </Typography>
            </Box>

            {/* 5. 専門家アドバイス＆まとめ */}
            <Box id="expert-tips" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    5. 専門家アドバイス＆まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    生活音トラブルは「お互いさま」精神と、ちょっとした工夫・配慮でぐっと減らすことができます。<br />
                    最後に、より快適なルームシェアを実現するためのポイントをまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                                <TipsAndUpdatesIcon color="primary" sx={{ mr: 1 }} />
                                快適シェア生活のための3つの心得
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="小さな音でも気にしすぎず、寛容な気持ちを忘れない" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="自分の生活音も常に意識し、他人への配慮を続ける" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="トラブルは早期発見・早期対処が鉄則" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                                <GavelIcon color="primary" sx={{ mr: 1 }} />
                                専門家のワンポイント
                            </Typography>
                            <Typography variant="body2">
                                生活音対策は「100点満点」を目指すより、「みんなが心地よく過ごせる70点」を目標に。完璧な静寂は無理でも、お互いの妥協点を探すことが大切です。<br />
                                また、半年ごとに「生活音ルールレビュー会」を開くことで、変化するライフスタイルにも柔軟に対応しましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 4 }} />
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.15rem', mb: 2 }}>
                    ルームシェアの「音ストレス」は、ちょっとした工夫と対話で必ず減らせます。<br />
                    あなたのシェア生活が、さらに快適で楽しいものになりますように！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-seikatsuon-trouble-countermeasures',
    slug: 'roomshare-seikatsuon-trouble-countermeasures',
    title: 'ルームシェアでよくある生活音トラブル対策集',
    excerpt: '生活音の種類、防音グッズ活用、トラブル回避コミュニケーション、ルール作りの重要性など、ルームシェアでよくある生活音トラブルへの具体的な対策を専門的に解説します。',
    coverImage: '/images/blog/cover-roomshare-sound_roomshare-seikatsuon-trouble-countermeasures.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目のテーブルや観葉植物が温かみを加え、快適な空間の中で音の配慮が感じられる雰囲気。',
    publishedAt: '2024-06-10T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '生活音対策', '暮らしの知恵'],
    tags: ['生活音', '防音グッズ', 'ルール作り', 'シェアハウス', 'コミュニケーション'],
    author: {
        name: '山本 さやか',
        avatar: '/images/avatars/sayaka-yamamoto.jpg',
        bio: 'ルームシェア歴10年以上。シェアハウス運営や住まい選びのアドバイザーとして、多様なトラブル解決経験を持つ。共生を楽しむノウハウを多くの人に伝えたい。',
        profilePicture: '/images/avatars/sayaka-yamamoto.jpg'
    },
    viewCount: 1423,
    contentComponent: PostContent
};

export default post;